import React, { useState } from 'react';
import {
    X, Database, TableProperties, Globe, PlugZap, Upload, Cloud,
    Server, Table, FileSpreadsheet, FileUp, Search, Building2,
    Workflow, Boxes, HardDrive, CloudCog
} from 'lucide-react';

interface SourceTypeSelectorModalProps {
    onClose: () => void;
    onSelect: (sourceType: string) => void;
    currentSelection?: string;
}

interface SourceTypeCard {
    id: string;
    label: string;
    description: string;
    icon: React.ReactNode;
    category: string;
}

const sourceCategories = [
    {
        name: 'SQL Databases',
        icon: <Database className="w-5 h-5" />,
        sources: [
            {
                id: 'SqlServer',
                label: 'SQL Server',
                description: 'Microsoft SQL Server database',
                icon: <Database className="w-6 h-6 text-cyan-400" />,
            },
            {
                id: 'PostgreSQL',
                label: 'PostgreSQL',
                description: 'Open source relational database',
                icon: <Database className="w-6 h-6 text-cyan-500" />,
            },
            {
                id: 'MySQL',
                label: 'MySQL',
                description: 'Popular open source database',
                icon: <Database className="w-6 h-6 text-blue-500" />,
            },
            {
                id: 'Oracle',
                label: 'Oracle',
                description: 'Enterprise database solution',
                icon: <Database className="w-6 h-6 text-red-500" />,
            },
            {
                id: 'SQLite',
                label: 'SQLite',
                description: 'Lightweight embedded database',
                icon: <Database className="w-6 h-6 text-gray-400" />,
            },
            {
                id: 'Snowflake',
                label: 'Snowflake',
                description: 'Cloud data warehouse',
                icon: <Cloud className="w-6 h-6 text-blue-200" />,
            },
        ]
    },
    {
        name: 'NoSQL Databases',
        icon: <Boxes className="w-5 h-5" />,
        sources: [
            {
                id: 'NoSQL_MongoDB',
                label: 'MongoDB',
                description: 'Document-oriented NoSQL database',
                icon: <TableProperties className="w-6 h-6 text-green-400" />,
            },
            {
                id: 'Firestore',
                label: 'Firestore',
                description: 'Google Cloud NoSQL database',
                icon: <TableProperties className="w-6 h-6 text-red-400" />,
            },
        ]
    },
    {
        name: 'Cloud Storage',
        icon: <CloudCog className="w-5 h-5" />,
        sources: [
            {
                id: 'S3_Bucket',
                label: 'AWS S3',
                description: 'Amazon cloud object storage',
                icon: <Cloud className="w-6 h-6 text-orange-400" />,
            },
            {
                id: 'Azure_Blob',
                label: 'Azure Blob',
                description: 'Microsoft cloud storage',
                icon: <Cloud className="w-6 h-6 text-blue-400" />,
            },
            {
                id: 'Google_Drive',
                label: 'Google Drive',
                description: 'Store, share, and collaborate',
                icon: <HardDrive className="w-6 h-6 text-green-500" />,
            },
            {
                id: 'OneDrive',
                label: 'OneDrive',
                description: 'Microsoft cloud storage',
                icon: <Cloud className="w-6 h-6 text-blue-500" />,
            },
            {
                id: 'FTP_SFTP',
                label: 'FTP/SFTP',
                description: 'File transfer protocol',
                icon: <Server className="w-6 h-6 text-purple-400" />,
            },
        ]
    },
    {
        name: 'APIs & Integration',
        icon: <Workflow className="w-5 h-5" />,
        sources: [
            {
                id: 'API',
                label: 'REST API',
                description: 'Connect to any REST API',
                icon: <Globe className="w-6 h-6 text-yellow-400" />,
            },
            {
                id: 'Webhook',
                label: 'Webhook',
                description: 'Real-time event notifications',
                icon: <PlugZap className="w-6 h-6 text-pink-400" />,
            },
        ]
    },
    {
        name: 'Business Applications',
        icon: <Building2 className="w-5 h-5" />,
        sources: [
            {
                id: 'Salesforce',
                label: 'Salesforce',
                description: 'CRM and cloud platform',
                icon: <Building2 className="w-6 h-6 text-blue-500" />,
            },
            {
                id: 'Dynamics',
                label: 'Dynamics 365',
                description: 'Microsoft business apps',
                icon: <Building2 className="w-6 h-6 text-cyan-500" />,
            },
            {
                id: 'Google_Sheets',
                label: 'Google Sheets',
                description: 'Collaborative spreadsheets',
                icon: <TableProperties className="w-6 h-6 text-green-400" />,
            },
            {
                id: 'SAP',
                label: 'SAP',
                description: 'SAP enterprise software',
                icon: <TableProperties className="w-6 h-6 text-green-400" />,
            },
        ]
    },
    {
        name: 'File Uploads',
        icon: <HardDrive className="w-5 h-5" />,
        sources: [
            {
                id: 'CSV_Upload',
                label: 'CSV Upload',
                description: 'Upload CSV files',
                icon: <Upload className="w-6 h-6 text-orange-400" />,
            },
            {
                id: 'CSV',
                label: 'CSV',
                description: 'Comma-separated values',
                icon: <Table className="w-6 h-6 text-white" />,
            },
            {
                id: 'Excel',
                label: 'Excel',
                description: 'Microsoft Excel files',
                icon: <FileSpreadsheet className="w-6 h-6 text-green-500" />,
            },
            {
                id: 'File',
                label: 'File',
                description: 'Generic file upload',
                icon: <FileUp className="w-6 h-6 text-gray-400" />,
            },

        ]
    },
];

const SourceTypeSelectorModal: React.FC<SourceTypeSelectorModalProps> = ({
    onClose,
    onSelect,
    currentSelection
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    const filteredCategories = sourceCategories.map(category => ({
        ...category,
        sources: category.sources.filter(source =>
            source.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
            source.description.toLowerCase().includes(searchQuery.toLowerCase())
        )
    })).filter(category => category.sources.length > 0);

    const displayCategories = selectedCategory
        ? filteredCategories.filter(c => c.name === selectedCategory)
        : filteredCategories;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[100] p-4">
            <div className="bg-neutral-900 rounded-lg w-full max-w-4xl max-h-[85vh] overflow-hidden border border-neutral-800 shadow-2xl">
                {/* Header */}
                <div className="bg-neutral-950 border-b border-neutral-800 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h2 className="text-xl font-bold text-white">Select Data Source</h2>
                            <p className="text-gray-400 text-xs mt-1">
                                Choose from various data sources to connect
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-neutral-800 rounded-lg"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Search Bar */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search data sources..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-neutral-800 border border-neutral-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
                        />
                    </div>
                </div>

                {/* Category Pills */}
                <div className="bg-neutral-900 border-b border-neutral-800 px-4 py-3">
                    <div className="flex gap-2 flex-wrap">
                        <button
                            onClick={() => setSelectedCategory(null)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all text-xs ${selectedCategory === null
                                ? 'bg-indigo-600 text-white'
                                : 'bg-neutral-800 text-gray-300 hover:bg-neutral-700'
                                }`}
                        >
                            All Sources
                        </button>
                        {sourceCategories.map((category) => (
                            <button
                                key={category.name}
                                onClick={() => setSelectedCategory(
                                    selectedCategory === category.name ? null : category.name
                                )}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all text-xs ${selectedCategory === category.name
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-neutral-800 text-gray-300 hover:bg-neutral-700'
                                    }`}
                            >
                                <span className="w-3.5 h-3.5">{category.icon}</span>
                                <span className="font-medium">{category.name}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="overflow-y-auto p-4 space-y-6" style={{ maxHeight: 'calc(85vh - 220px)' }}>
                    {displayCategories.length === 0 ? (
                        <div className="text-center py-8">
                            <Search className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                            <p className="text-gray-400 text-base">No sources found</p>
                            <p className="text-gray-500 text-xs mt-1">
                                Try adjusting your search query
                            </p>
                        </div>
                    ) : (
                        displayCategories.map((category) => (
                            <div key={category.name}>
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="text-indigo-400 w-4 h-4">{category.icon}</div>
                                    <h3 className="text-sm font-semibold text-white">
                                        {category.name}
                                    </h3>
                                    <div className="flex-1 h-px bg-neutral-800"></div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {category.sources.map((source) => (
                                        <button
                                            key={source.id}
                                            onClick={() => onSelect(source.id)}
                                            className={`group relative bg-neutral-800 rounded-lg p-3 text-left transition-all duration-200 hover:scale-105 hover:shadow-xl border-2 ${currentSelection === source.id
                                                ? 'border-indigo-500 bg-indigo-950/30'
                                                : 'border-transparent hover:border-indigo-500/50'
                                                }`}
                                        >
                                            {/* Selection Indicator */}
                                            {currentSelection === source.id && (
                                                <div className="absolute top-2 right-2 w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center">
                                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                            )}

                                            {/* Icon */}
                                            <div className="mb-2 transform group-hover:scale-110 transition-transform duration-200 w-6 h-6">
                                                {source.icon}
                                            </div>

                                            {/* Content */}
                                            <h4 className="text-white font-semibold text-sm mb-0.5 group-hover:text-indigo-400 transition-colors">
                                                {source.label}
                                            </h4>
                                            <p className="text-gray-400 text-xs leading-relaxed">
                                                {source.description}
                                            </p>

                                            {/* Hover Effect */}
                                            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/0 to-purple-600/0 group-hover:from-indigo-600/10 group-hover:to-purple-600/10 rounded-lg transition-all duration-200"></div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default SourceTypeSelectorModal;